import * as React from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useUserOrg from '../../Authentication/components/hooks/useUserOrg';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import {
    Segmented,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
    TSkeleton,
} from '../components/table';
import { ConfirmDialog, Dialog, Field, TextInput } from '../components/forms';
import { PageHeadActions, SubNav } from '../components/nav';
import { useList } from '../components/roster';
import Timeline, { TLBooking, TLRow } from '../components/timeline';
import { addDays, dayStart, endDayOf, mondayOf } from '../components/calendar';
import { useEventDetails, useProductionRange } from '../lib/schedule';
import '../calendar.css';

/*
 * Devices, with tabs in place of the classic second sidebar.
 * Create flows still open the existing screens.
 */

type Tab = 'keys' | 'schedule' | 'groups' | 'autoenrollment' | 'software';

const TABS: Array<{ id: Tab; label: string }> = [
    { id: 'keys', label: 'Keys' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'groups', label: 'Groups' },
    { id: 'autoenrollment', label: 'Auto enrollment' },
    { id: 'software', label: 'Software' },
];

const SOFTWARE: Array<{ id: string; label: string; platform: string }> = [
    { id: 'smileport-adapter', label: 'Smileport Adapter', platform: 'Windows' },
    { id: 'mac-client', label: 'Mac Client', platform: 'macOS' },
    { id: 'mac-upload-utility', label: 'Mac Upload Utility', platform: 'macOS' },
    { id: 'mac-processing-utility', label: 'Mac Processing Utility', platform: 'macOS' },
    { id: 'mac-sharing-utility', label: 'Mac Sharing Utility', platform: 'macOS' },
    { id: 'romr-android', label: 'ROMR', platform: 'Android' },
];

const ONLINE_MS = 10 * 60 * 1000;

function seen(iso?: string): string {
    if (!iso) return 'Never seen';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return 'Never seen';
    if (ms < 90 * 1000) return 'Seen just now';
    if (ms < 90 * 60 * 1000) return `Seen ${Math.round(ms / 60000)}m ago`;
    if (ms < 36 * 3600 * 1000) return `Seen ${Math.round(ms / 3600000)}h ago`;
    return `Seen ${Math.round(ms / 86400000)}d ago`;
}

/** Loading, failed, and empty states around a list. */
function ListState({
    rows,
    failed,
    what,
    reload,
    children,
}: {
    rows: unknown[] | null;
    failed: boolean;
    what: string;
    reload: () => void;
    children: React.ReactNode;
}) {
    if (failed)
        return (
            <TCard>
                <TEmpty message={`Couldn’t load ${what}.`} actionLabel="Retry" onAction={reload} />
            </TCard>
        );
    if (rows === null)
        return (
            <TCard>
                <TSkeleton rows={4} />
            </TCard>
        );
    if (rows.length === 0)
        return (
            <TCard>
                <TEmpty message={`No ${what} yet.`} />
            </TCard>
        );
    return <TCard>{children}</TCard>;
}

export default function DevicesV3() {
    const navigate = useNavigate();
    const location = useLocation();
    const { orgId } = useUserOrg();
    const api = useAuthenticatedApi();

    const hashTab = location.hash.replace('#', '') as Tab;
    const [tab, setTab] = useState<Tab>(
        TABS.some((t) => t.id === hashTab) ? hashTab : 'keys',
    );
    const selectTab = (id: string) => {
        setTab(id as Tab);
        window.history.replaceState(null, '', `#${id}`);
    };
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState<'all' | 'online' | 'offline'>('all');

    const keys = useList<any>(() =>
        orgId ? api.get(`${orgId}/device-keys`).then((r: any) => r.data) : null,
        [orgId],
    );
    const groups = useList<any>(() =>
        orgId ? api.get(`${orgId}/device-groups`).then((r: any) => r.data) : null,
        [orgId],
    );
    const enroll = useList<any>(
        () =>
            orgId
                ? api.get(`${orgId}/device-registration-autoenroll/`).then((r: any) => r.data)
                : null,
        [orgId],
    );
    const software = useList<any>(() =>
        Promise.all(
            SOFTWARE.map((s) =>
                api
                    .get(`/software/${s.id}`)
                    .then((r: any) => ({ ...s, ...(r.data ?? {}) }))
                    .catch(() => ({ ...s, failed: true })),
            ),
        ),
    );

    const q = query.trim().toLowerCase();
    const isOnline = (k: any) =>
        !!k.lastUsed && Date.now() - new Date(k.lastUsed).getTime() < ONLINE_MS;
    const keyRows = useMemo(
        () =>
            (keys.rows ?? [])
                .filter(
                    (k) =>
                        !q ||
                        (k.name ?? '').toLowerCase().includes(q) ||
                        (k.keyId ?? '').toLowerCase().includes(q),
                )
                .filter((k) =>
                    status === 'all' ? true : (status === 'online') === isOnline(k),
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [keys.rows, q, status],
    );
    const online = (keys.rows ?? []).filter(
        (k) =>
            k.lastUsed &&
            Date.now() - new Date(k.lastUsed).getTime() < ONLINE_MS,
    ).length;

    /* Remove revokes the key and can't be undone, so it confirms. */
    const [renaming, setRenaming] = useState<any>(null);
    const [newName, setNewName] = useState('');
    const [removing, setRemoving] = useState<any>(null);
    const rename = async () => {
        const k = renaming;
        const name = newName.trim();
        if (!k || !name) return;
        setRenaming(null);
        keys.setRows((list) =>
            (list ?? []).map((r) => (r.id === k.id ? { ...r, name } : r)),
        );
        try {
            await api.post(
                `${orgId}/device-keys/rename`,
                JSON.stringify({ keyId: k.keyId, name }),
            );
        } catch {
            keys.reload();
        }
    };
    const remove = async (k: any) => {
        keys.setRows((list) => (list ?? []).filter((r) => r.id !== k.id));
        try {
            await api.delete(`${orgId}/device-keys/${k.id}`);
        } catch {
            keys.reload();
        }
    };

    /* Schedule: bookings from each event's assigned devices. Unbooked rows read "free". */
    const [tlStart, setTlStart] = useState(() => mondayOf(Date.now()));
    const range = useProductionRange(addDays(tlStart, -31), addDays(tlStart, 13), tab === 'schedule');
    const events = (range.rows ?? []).filter((r) => !r.isProvisional && r.startAt);
    const assigned = useEventDetails(events.map((r) => r.activationId), ['devices']);
    const tlBookings: TLBooking[] = events.flatMap((r) =>
        (assigned[r.activationId]?.devices ?? []).map((d: any) => ({
            id: `${r.activationId}-${d.deviceKeyId}`,
            rowId: d.deviceKeyId,
            name: r.name,
            startDay: dayStart(r.startAt!),
            endDay: endDayOf(r.endAt ?? r.startAt!, dayStart(r.startAt!)),
            kind: 'confirmed' as const,
            href: `/activations/${r.activationId}`,
        })),
    );
    const tlEnd = addDays(tlStart, 13);
    /* search only — the Keys tab's Online/Offline filter doesn't apply here */
    const tlKeys = (keys.rows ?? []).filter(
        (k) => !q || (k.name ?? '').toLowerCase().includes(q) || (k.keyId ?? '').toLowerCase().includes(q),
    );
    const tlRows: TLRow[] = tlKeys.map((k) => {
        const busy = tlBookings.some((b) => b.rowId === k.keyId && b.endDay >= tlStart && b.startDay <= tlEnd);
        return { id: k.keyId, name: k.name ?? k.keyId, sub: busy ? seen(k.lastUsed) : `${seen(k.lastUsed)} · free` };
    });

    const counts: Record<Tab, number | undefined> = {
        schedule: undefined,
        keys: keys.rows?.length,
        groups: groups.rows?.length,
        autoenrollment: enroll.rows?.length,
        software: undefined,
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Devices"
                sub={
                    keys.rows
                        ? `${keys.rows.length} devices · ${online} online`
                        : 'Your fleet'
                }
            />
            <SubNav
                items={TABS.map((t) => ({
                    ...t,
                    href: `/device-management#${t.id}`,
                    count: counts[t.id],
                }))}
                active={tab}
                onSelect={selectTab}
                label="Devices sections"
            />

            {tab === 'keys' && (
                <>
                    <Toolbar
                        searchValue={query}
                        onSearch={setQuery}
                        searchPlaceholder="Search devices"
                        filterCount={0}
                        inline={
                            <Segmented
                                label="Status"
                                options={[
                                    { value: 'all', label: 'All' },
                                    { value: 'online', label: 'Online' },
                                    { value: 'offline', label: 'Offline' },
                                ]}
                                value={status}
                                onChange={setStatus}
                            />
                        }
                        primaryLabel="New device key"
                        onPrimary={() =>
                            navigate('/device-management/device-keys/create')
                        }
                    />
                    <ListState rows={keys.rows} failed={keys.failed} what="devices" reload={keys.reload}>
                        {keyRows.length === 0 ? (
                            <TEmpty
                                message={
                                    q ? `Nothing matches “${query}”.` : `No ${status} devices.`
                                }
                            />
                        ) : (
                            keyRows.map((k, i) => {
                                const on =
                                    !!k.lastUsed &&
                                    Date.now() - new Date(k.lastUsed).getTime() < ONLINE_MS;
                                return (
                                    <TRow
                                        key={k.id}
                                        index={i}
                                        name={k.name}
                                        meta={k.keyId}
                                        metaDate={seen(k.lastUsed)}
                                        date={seen(k.lastUsed)}
                                        status={
                                            on ? (
                                                <span className="ready">Online</span>
                                            ) : (
                                                'Offline'
                                            )
                                        }
                                        href={`/orgs/${orgId}/device-telemetry/${k.keyId}`}
                                        onOpen={() =>
                                            navigate(`/orgs/${orgId}/device-telemetry/${k.keyId}`)
                                        }
                                        menuItems={[
                                            {
                                                label: 'Remote',
                                                onClick: () =>
                                                    navigate(`/orgs/${orgId}/devices/${k.keyId}/remote`),
                                            },
                                            {
                                                label: 'Telemetry',
                                                onClick: () =>
                                                    navigate(`/orgs/${orgId}/device-telemetry/${k.keyId}`),
                                            },
                                            {
                                                label: 'Rename…',
                                                onClick: () => {
                                                    setRenaming(k);
                                                    setNewName(k.name ?? '');
                                                },
                                            },
                                            {
                                                label: 'Remove…',
                                                onClick: () => setRemoving(k),
                                            },
                                        ]}
                                    />
                                );
                            })
                        )}
                    </ListState>
                    {keys.rows && (
                        <TFoot>
                            {q || status !== 'all'
                                ? `${keyRows.length} of ${keys.rows.length} · filtered`
                                : `${keys.rows.length} devices · ${online} online · online = seen in the last 10 minutes`}
                        </TFoot>
                    )}
                </>
            )}

            {tab === 'schedule' && (
                <>
                    <Toolbar
                        searchValue={query}
                        onSearch={setQuery}
                        searchPlaceholder="Search devices"
                        filterCount={0}
                    />
                    {range.failed ? (
                        <TCard>
                            <TEmpty message="Couldn’t load the schedule." actionLabel="Retry" onAction={range.reload} />
                        </TCard>
                    ) : (
                        <Timeline
                            title="Device schedule"
                            what="devices"
                            rows={tlRows}
                            bookings={tlBookings}
                            start={tlStart}
                            onShift={(n) => setTlStart((t) => addDays(t, 7 * n))}
                            onToday={() => setTlStart(mondayOf(Date.now()))}
                            navigate={navigate}
                            loading={keys.rows === null || range.rows === null}
                        />
                    )}
                    <TFoot>Assignments come from each event · view only</TFoot>
                </>
            )}

            {tab === 'groups' && (
                <>
                    <div className="toolbar">
                        <button
                            className="tb-btn"
                            onClick={() => navigate('/device-management/device-invites/create')}
                        >
                            Invite device
                        </button>
                        <button
                            className="tb-new"
                            onClick={() => navigate('/device-management/device-groups/create')}
                        >
                            New group
                        </button>
                    </div>
                    <ListState rows={groups.rows} failed={groups.failed} what="groups" reload={groups.reload}>
                        {(groups.rows ?? []).map((g, i) => {
                            const n = g.devices?.length ?? 0;
                            const inv = g.invites?.length ?? 0;
                            const meta = `${n} ${n === 1 ? 'device' : 'devices'}${inv ? ` · ${inv} invited` : ''}`;
                            const to = `/device-management/device-groups/${g.id}/device-keys`;
                            return (
                                <TRow
                                    key={g.id}
                                    index={i}
                                    name={g.name}
                                    meta={meta}
                                    metaDate=""
                                    date=""
                                    status=""
                                    href={to}
                                    onOpen={() => navigate(to)}
                                    menuItems={[
                                        { label: 'Devices', onClick: () => navigate(to) },
                                        {
                                            label: 'Invites',
                                            onClick: () =>
                                                navigate(`/device-management/device-groups/${g.id}/invites`),
                                        },
                                    ]}
                                />
                            );
                        })}
                    </ListState>
                    {groups.rows && <TFoot>{groups.rows.length} groups</TFoot>}
                </>
            )}

            {tab === 'autoenrollment' && (
                <>
                    <p className="pg-sub" style={{ marginTop: -4 }}>
                        Pre-registered serials join this org the first time
                        the device connects.
                    </p>
                    <ListState rows={enroll.rows} failed={enroll.failed} what="registrations" reload={enroll.reload}>
                        {(enroll.rows ?? []).map((r, i) => (
                            <TRow
                                key={r.id}
                                index={i}
                                name={r.deviceName || r.deviceId}
                                meta={[r.deviceId, r.groupNames].filter(Boolean).join(' · ')}
                                metaDate=""
                                date={
                                    r.createdAt
                                        ? new Date(r.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                                        : ''
                                }
                                status={r.counter ? `Joined ${r.counter}×` : 'Waiting'}
                                onOpen={() => {}}
                                menuItems={[]}
                            />
                        ))}
                    </ListState>
                    {enroll.rows && <TFoot>{enroll.rows.length} registrations</TFoot>}
                </>
            )}

            {tab === 'software' && (
                <>
                    <p className="pg-sub" style={{ marginTop: -4 }}>
                        The current release of each capture and utility app.
                    </p>
                    <ListState rows={software.rows} failed={software.failed} what="releases" reload={software.reload}>
                        {(software.rows ?? []).map((s, i) => (
                            <TRow
                                key={s.id}
                                index={i}
                                name={s.label}
                                meta={s.platform}
                                metaDate={s.version ? `v${s.version}` : ''}
                                date={s.version ? `v${s.version}` : ''}
                                status={
                                    s.bundleUri ? 'Download' : s.failed ? 'Unavailable' : ''
                                }
                                href={s.bundleUri}
                                onOpen={() => s.bundleUri && window.open(s.bundleUri, '_blank')}
                                menuItems={
                                    s.bundleUri
                                        ? [
                                              { label: 'Download', onClick: () => window.open(s.bundleUri, '_blank') },
                                              { label: 'Copy link', onClick: () => navigator.clipboard?.writeText(s.bundleUri) },
                                          ]
                                        : []
                                }
                            />
                        ))}
                    </ListState>
                </>
            )}

            <Dialog
                open={renaming !== null}
                title="Rename device"
                onClose={() => setRenaming(null)}
                onSubmit={rename}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setRenaming(null)}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={!newName.trim()}>
                            Rename
                        </button>
                    </>
                }
            >
                <Field label="Name" help="Shown on the device list and assignments.">
                    <TextInput
                        autoFocus
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onFocus={(e) => e.target.select()}
                    />
                </Field>
            </Dialog>
            <ConfirmDialog
                open={removing !== null}
                title={`Remove “${removing?.name ?? ''}”?`}
                body="This revokes the device key. The device stops uploading immediately and has to be set up again."
                actionLabel="Remove device"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
        </AppShell>
    );
}
